import type { SelectGroup, SelectOption } from './types.js';

export const GROUP_PADDING = 1;

export interface ListItem<T = unknown> {
  itemIndex: number;
  groupIndex: number;
  rowIndex: number;
  group: SelectGroup<T>;
  item: SelectOption<T>;
}

const getSizeOfAddedPart = (
  padding: number,
  currentItem: ListItem,
  prevItem?: ListItem
) => {
  let accumulatedSize = 0;
  if (!prevItem) {
    // Adding row for group title
    accumulatedSize += 1;
    // Adding row for item title
    accumulatedSize += 1;
  } else {
    if (prevItem.groupIndex !== currentItem.groupIndex) {
      // Adding row for group padding
      accumulatedSize += padding;
      // Adding row for group title
      accumulatedSize += 1;
    }
    // Adding row for item title
    accumulatedSize += 1;
  }

  return accumulatedSize;
};

export function getListItems<T>(
  groups: Array<SelectGroup<T>>
): Array<ListItem<T>> {
  let rowIndex = 0;
  return groups.reduce<Array<ListItem<T>>>((listItems, group, groupIndex) => {
    rowIndex += 1;
    for (const item of group.items) {
      listItems.push({
        itemIndex: listItems.length,
        rowIndex,
        groupIndex,
        group,
        item,
      });
      rowIndex += 1;
    }
    rowIndex += GROUP_PADDING;

    return listItems;
  }, []);
}

export const getRowCount = (listItems: ListItem[]) =>
  listItems.reduce(
    (count, item, index, array) =>
      count +
      (index
        ? getSizeOfAddedPart(GROUP_PADDING, item, array[index - 1])
        : getSizeOfAddedPart(GROUP_PADDING, item)),
    0
  );

export function getVisibleListItems<T>(
  staringIndex: number,
  listItems: Array<ListItem<T>>,
  limit: number,
  reverse?: boolean
): Array<ListItem<T>> {
  if (staringIndex >= listItems.length) {
    return [];
  }

  const visibleListItems: Array<ListItem<T>> = [];
  let accumulatedSize = 0;
  if (reverse) {
    let currentItemIndex = staringIndex;

    while (currentItemIndex >= 0) {
      const currentItem = listItems[currentItemIndex]!;
      const sizeOfPart = getSizeOfAddedPart(
        GROUP_PADDING,
        currentItem,
        visibleListItems[0]
      );

      if (accumulatedSize + sizeOfPart <= limit) {
        visibleListItems.unshift(currentItem);
        accumulatedSize += sizeOfPart;
        currentItemIndex -= 1;
      } else {
        break;
      }
    }

    currentItemIndex = staringIndex + 1;
    if (accumulatedSize <= limit) {
      while (currentItemIndex < listItems.length) {
        const currentItem = listItems[currentItemIndex]!;
        const sizeOfPart = getSizeOfAddedPart(
          GROUP_PADDING,
          currentItem,
          visibleListItems.at(-1)
        );

        if (accumulatedSize + sizeOfPart <= limit) {
          visibleListItems.push(currentItem);
          accumulatedSize += sizeOfPart;
          currentItemIndex += 1;
        } else {
          break;
        }
      }
    }
  } else {
    let currentItemIndex = staringIndex;
    while (currentItemIndex < listItems.length) {
      const currentItem = listItems[currentItemIndex]!;
      const sizeOfPart = getSizeOfAddedPart(
        GROUP_PADDING,
        currentItem,
        visibleListItems.at(-1)
      );

      if (accumulatedSize + sizeOfPart <= limit) {
        visibleListItems.push(currentItem);
        accumulatedSize += sizeOfPart;
        currentItemIndex += 1;
      } else {
        break;
      }
    }

    currentItemIndex = staringIndex - 1;

    if (accumulatedSize <= limit) {
      while (currentItemIndex >= 0) {
        const currentItem = listItems[currentItemIndex]!;
        const sizeOfPart = getSizeOfAddedPart(
          GROUP_PADDING,
          currentItem,
          visibleListItems[0]
        );

        if (accumulatedSize + sizeOfPart <= limit) {
          visibleListItems.unshift(currentItem);
          accumulatedSize += sizeOfPart;
          currentItemIndex -= 1;
        } else {
          break;
        }
      }
    }
  }

  return visibleListItems;
}
